import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { addDays, format, parseISO, startOfDay } from "date-fns"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useTranslation } from "react-i18next"
import {
  AlertTriangle,
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FilePlus2,
  FileText,
  Loader2,
  PencilLine,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Truck,
  UserRound,
  X,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { AutocompleteGooglePlaces } from "@/components/maps/AutocompleteGooglePlaces"
import { matchesPlateFilter } from "@/components/timeline/matchesPlateFilter"
import {
  buildTimelineData,
  TimelineChain,
  TimelineTone,
} from "@/components/timeline/buildTimelineData"
import { cn } from "@/lib/utils"
import { getDateFnsLocale } from "@/lib/i18n/format"
import {
  selectAllDriverRouteAndLocationDetailsMap,
  selectAllDriversJids,
  selectDriversJids,
} from "@/reducers/accountChatsSelectors"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { formatDuration } from "@/components/RouteStopView"
import { RouteStop } from "@/reducers/routes2gSlice"
import { selectChats } from "@/reducers/chatsSliceSelectors"
import { selectArchivedChats } from "@/reducers/chatsSettingsSelectors"
import { officeProfiles } from "@/reducers/profilesSliceSelectors"
import {
  assignedCountByDispatcherOf,
  firstPickupStop,
  lastDeliveryStop,
  PlannedOrder,
  selectPlannedOrders,
  unassignedOrdersOf,
} from "@/reducers/plannedOrdersSlice"
import {
  assignPlannedOrderToDispatcherAndSync,
  assignPlannedOrderToVehicleAndSync,
  enrichPlannedOrderStops,
  fetchPlannedOrders,
  savePlannedOrder,
  unassignPlannedOrderAndSync,
} from "@/reducers/plannedOrdersThunks"
import { useTimelineFeeds } from "./useTimelineFeeds"
import {
  ProximityPoint,
  rankVehiclesByProximity,
  SuggestionCandidate,
  suggestVehiclesForOrder,
} from "./vehicleSuggestions"
import { buildRouteBar, type RouteBar } from "./routeBar"
import RouteSegmentDialog from "./RouteSegmentDialog"
import { buildRankingCandidates } from "./rankingCandidates"
import {
  approachLabel,
  isDateWithinTimelineWindow,
  parsePlanningPoint,
} from "./plannerPointRanking"
import { APPROACH_TOP_N, selectApproachOrigins } from "./approachOrigins"
import { useApproachDistances } from "./useApproachDistances"
import { countryCodeToFlagEmoji, plannedOrderFlags } from "./plannerFlags"
import {
  matchesMyCompanies,
  matchesVehicleCompanyFilter,
  scopeOrdersToDispatcher,
  vehicleCompanyNames,
} from "./plannerFilters"
import { selectPlannerCompanies } from "@/reducers/settingsSlice"
import PlannedOrdersPanel from "./PlannedOrdersPanel"
import PlannedOrderCreateFlow, {
  PlannedOrderCreateMode,
} from "./PlannedOrderCreateFlow"
import PlannedOrderDetailPanel, {
  plannedOrderRouteLabel,
} from "./PlannedOrderDetailPanel"
import {
  BAR_HEIGHT,
  DAY_IN_MS,
  DAY_WIDTH,
  DRIVER_COLUMN_WIDTH,
  HOUR_TICKS,
  LANE_SPACING,
  ROW_MIN_HEIGHT,
  ROW_PADDING_Y,
} from "./plannerGeometry"
import TimelineBar from "./TimelineBar"
import { chainProgress } from "./chainProgress"
import {
  barLabel,
  orderCountLabel,
  orderReferenceNumber,
  stopCountLabel,
} from "./plannerLabels"

// Feed chains use "UN" as the unknown-country sentinel — no flag for those.
const chainFlag = (code?: string): string | null =>
  code && code !== "UN" ? countryCodeToFlagEmoji(code) : null

const chainToneClasses: Record<TimelineTone, string> = {
  danger:
    "border-rose-300 bg-rose-100 text-rose-950 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100",
  info: "border-sky-300 bg-sky-100 text-sky-950 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100",
  neutral:
    "border-slate-300 bg-slate-100 text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100",
  success:
    "border-emerald-300 bg-emerald-100 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100",
  warning:
    "border-amber-300 bg-amber-100 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100",
}

// Today's column gets a tinted band the whole height of the grid, the way the
// mockup marks it — across eight days of columns the thin now-line alone was
// easy to lose.
const TODAY_COLUMN_TINT = "bg-sky-50/70 dark:bg-sky-950/20"

// Section bands across the timeline share one look: a full-width tinted strip
// with a plain (non-uppercase) label. Amber ties the dispatcher band to the
// amber parked-order bars underneath it, so the block reads as one stage.
const SECTION_BAND = "flex border-y"
const SECTION_BAND_LABEL = "sticky left-0 z-10 px-3 py-1.5 text-sm font-medium"

type PlannerBar = {
  order: PlannedOrder
  startOffset: number
  endOffset: number
}

type PlannerRow = {
  key: string
  chatJid?: string
  plates?: string
  driverName: string
  roomName?: string
  chains: TimelineChain[]
  laneCount: number
  routeBar: RouteBar | null
  plannedBars: PlannerBar[]
  // Companies this vehicle works for, from its transports and its planned
  // orders alike — feeds both the "firma" options and the row filter, so the
  // dropdown can never offer a company no row can match.
  companies: string[]
}

const parseTime = (value?: string): Date | undefined => {
  if (!value) return undefined
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

const toOffset = (date: Date, timelineStart: Date): number =>
  (date.getTime() - timelineStart.getTime()) / DAY_IN_MS

const buildPlannedBar = (
  order: PlannedOrder,
  timelineStart: Date,
): PlannerBar | null => {
  const pickupTime = parseTime(firstPickupStop(order)?.scheduled_arrival_time)
  const deliveryTime = parseTime(
    lastDeliveryStop(order)?.scheduled_arrival_time,
  )
  const start = pickupTime ?? deliveryTime
  if (!start) return null
  const end = deliveryTime ?? pickupTime ?? start
  const startOffset = toOffset(start, timelineStart)
  const endOffset = Math.max(toOffset(end, timelineStart), startOffset + 0.2)
  return { order, startOffset, endOffset }
}

const PlannerTimelineView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isPlannerRoleEnabled = Boolean(useFeatureFlagEnabled("planner-role"))
  const driversByJid = useAppSelector(selectAllDriverRouteAndLocationDetailsMap)
  const bookmarkedDriversByJid = useAppSelector(selectDriversJids)
  const allDriversByJid = useAppSelector(selectAllDriversJids)
  const archivedChatJids = useAppSelector(selectArchivedChats)
  const chats = useAppSelector(selectChats)
  const allOfficeProfiles = useAppSelector(officeProfiles)
  // officeProfiles has every "office"-role profile on the vhost, which also
  // catches Slickshift's own support/test accounts (name suffixed
  // "(Slickshift)", or plain support/bot names) that happen to carry that
  // role for admin access. There's no "is a real dispatcher" flag in the
  // data, so the best available signal is the JID's domain: a real
  // dispatcher's JID is on this same vhost, a Slickshift account's isn't.
  const currentVhost = window.XMPP?.jid?.domain
  const dispatchers = useMemo(
    () =>
      (currentVhost
        ? allOfficeProfiles.filter(
            (profile) => profile.jid.split("@")[1] === currentVhost,
          )
        : allOfficeProfiles
      ).filter((profile) => profile.jid.split("@")[0].includes(".")),
    [allOfficeProfiles, currentVhost],
  )
  const allPlannedOrders = useAppSelector(selectPlannedOrders)
  const plannerCompanies = useAppSelector(selectPlannerCompanies)
  const currentUserJid = window.XMPP?.jid?.bare().toString()
  // Nakładamy wybór DOKŁADNIE RAZ, na wejściu danych do Plannera. Pula
  // nieprzypisanych i liczniki dyspozytorów liczą się z już zawężonej listy —
  // filtrowanie osobno w każdym z nich skończyłoby się licznikiem w nagłówku
  // niezgodnym z listą pod nim.
  const myCompanyNames = useMemo(
    () => plannerCompanies.map((company) => company.name),
    [plannerCompanies],
  )
  const plannedOrders = useMemo(
    () =>
      allPlannedOrders.filter((order) =>
        matchesMyCompanies(order, myCompanyNames, currentUserJid),
      ),
    [allPlannedOrders, myCompanyNames, currentUserJid],
  )
  const unassignedOrders = useMemo(
    () => unassignedOrdersOf(plannedOrders),
    [plannedOrders],
  )
  const assignedCountByDispatcher = useMemo(
    () => assignedCountByDispatcherOf(plannedOrders),
    [plannedOrders],
  )

  // A regular dispatcher only ever works with their own bookmarked vehicles
  // — excluding archived ones, since archiving a chat never removes the
  // bookmark, so a long-tenured dispatcher's raw bookmark list balloons to
  // nearly their whole shipping history rather than the handful of vehicles
  // they actually work with day to day. The Planner role needs the whole
  // fleet regardless, same as it already sees every dispatcher's orders —
  // otherwise "all orders" would be paired with a vehicle list still
  // limited to the Planner's own bookmarks.
  const archivedChatJidSet = useMemo(
    () => new Set(archivedChatJids),
    [archivedChatJids],
  )
  const scopedDriverChatJids = useMemo(
    () =>
      Array.from(
        isPlannerRoleEnabled
          ? allDriversByJid.values()
          : bookmarkedDriversByJid.values(),
      )
        .filter((jid) => isPlannerRoleEnabled || !archivedChatJidSet.has(jid))
        .sort(),
    [
      isPlannerRoleEnabled,
      allDriversByJid,
      bookmarkedDriversByJid,
      archivedChatJidSet,
    ],
  )
  const scopedDriverChatJidSet = useMemo(
    () => new Set(scopedDriverChatJids),
    [scopedDriverChatJids],
  )
  // This scope must also bound which rows the planner timeline renders, not
  // just which live feeds get fetched — driversByJid on its own covers the
  // whole fleet, feeds or not.
  const drivers = useMemo(
    () =>
      isPlannerRoleEnabled
        ? Object.values(driversByJid)
        : Object.values(driversByJid).filter(
            (driver) =>
              driver.chatJid && scopedDriverChatJidSet.has(driver.chatJid),
          ),
    [driversByJid, isPlannerRoleEnabled, scopedDriverChatJidSet],
  )
  const {
    feeds,
    loading,
    error: timelineFeedsError,
    isAnyProviderEnabled,
    retry: retryTimelineFeeds,
  } = useTimelineFeeds(scopedDriverChatJids)

  // Pull the server copy on entry; local optimistic state stays authoritative
  // between refreshes.
  useEffect(() => {
    dispatch(fetchPlannedOrders())
  }, [dispatch])

  const myOrders = useMemo(
    () =>
      plannedOrders.filter(
        (order) =>
          order.assignmentStatus === "dispatcher" &&
          order.assignedDispatcherJid === currentUserJid,
      ),
    [plannedOrders, currentUserJid],
  )
  const dispatcherAssignedOrders = useMemo(
    () =>
      plannedOrders.filter((order) => order.assignmentStatus === "dispatcher"),
    [plannedOrders],
  )

  // One planner-wide search string: narrows the vehicle rows here and the order
  // lists in the right panel (mockup has a single "szukaj zlecenia lub pojazdu").
  const [plannerQuery, setPlannerQuery] = useState("")
  // Lifted out of PlannedOrdersPanel so the same selection also narrows the
  // vehicle rows on the left (bypassed while assigning — see filteredRows).
  const [companyFilter, setCompanyFilter] = useState<string[]>([])
  // Also lifted out of the panel, so both selects sit in the one filter bar.
  const [dispatcherFilter, setDispatcherFilter] = useState<string[]>([])
  // "Punkt planowania" (odpowiednik pola z lewego dolnego narożnika Marcosa):
  // dyspozytor wpisuje współrzędne i datę, a grid sortuje się po dojeździe.
  // Data jest osobnym stanem od viewDate — przewijanie osi czasu nie ma
  // przestawiać rankingu pod ręką.
  const [planningPoint, setPlanningPoint] = useState<ProximityPoint | null>(
    null,
  )
  const [planningDate, setPlanningDate] = useState<Date | null>(null)
  // AutocompleteGooglePlaces czyta initialValue tylko przy montowaniu (useState
  // seed) i nigdy się z nim nie resynchronizuje, więc wyczyszczenie pola wymaga
  // remountu — inaczej gasimy ranking, a w polu zostaje stary tekst.
  const [pointInputSeq, setPointInputSeq] = useState(0)
  const clearPointInput = () => setPointInputSeq((seq) => seq + 1)
  // "Dodaj zlecenie" — file input lives here (not in PlannedOrdersPanel) so
  // the native picker opens synchronously within the user gesture that
  // triggers it, same as the other two entry points.
  const addOrderFileInputRef = useRef<HTMLInputElement>(null)
  // seq forces a remount per request so re-picking the same mode re-triggers
  // the flow (e.g. reopening the native file picker after a cancel).
  const [createRequest, setCreateRequest] = useState<{
    mode: PlannedOrderCreateMode
    seq: number
    file?: File
  } | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  // Vehicle-assign mode: the timeline itself filters/sorts to fitting
  // vehicles and a row click assigns the order.
  const [assigningOrderId, setAssigningOrderId] = useState<string | null>(null)
  // Collapsible dispatcher lanes under the vehicle timeline
  const [isDispatcherSectionOpen, setDispatcherSectionOpen] = useState(true)
  const [selectedChain, setSelectedChain] = useState<{
    chain: TimelineChain
    rowLabel: string
  } | null>(null)
  const [selectedRouteSegment, setSelectedRouteSegment] = useState<{
    stops: RouteStop[]
    rowLabel: string
  } | null>(null)

  const selectedOrder =
    plannedOrders.find((order) => order.id === selectedOrderId) ?? null
  const assigningOrder =
    plannedOrders.find((order) => order.id === assigningOrderId) ?? null

  const now = new Date()
  const [viewDate, setViewDate] = useState(() => startOfDay(now))
  const timelineStart = viewDate
  // Dzień rankingu punktowego — jeden wybór dla kolejności wierszy, dla
  // tożsamości pomiaru dojazdu drogowego i dla pytania, czy o zajętości w tym
  // dniu cokolwiek wiemy.
  const planningDay = planningDate ?? viewDate

  const baseData = useMemo(
    () => buildTimelineData(drivers, feeds, chats, viewDate, t),
    [drivers, feeds, chats, viewDate, t],
  )

  const { rows, dateOffsets } = useMemo(() => {
    const baseRowsByChatJid = new Map(
      (baseData?.rows ?? [])
        .filter((row) => row.chatJid)
        .map((row) => [row.chatJid as string, row]),
    )
    const plannedBarsByChatJid = new Map<string, PlannerBar[]>()
    // Kept apart from the bars: an order with no usable pickup/delivery time
    // draws nothing, yet the vehicle still counts as working for its company.
    const plannedOrdersByChatJid = new Map<string, PlannedOrder[]>()
    for (const order of plannedOrders) {
      if (
        order.assignmentStatus !== "vehicle" ||
        !order.assignedVehicleChatJid
      ) {
        continue
      }
      const owned =
        plannedOrdersByChatJid.get(order.assignedVehicleChatJid) ?? []
      owned.push(order)
      plannedOrdersByChatJid.set(order.assignedVehicleChatJid, owned)

      const bar = buildPlannedBar(order, timelineStart)
      if (!bar) continue
      const bars = plannedBarsByChatJid.get(order.assignedVehicleChatJid) ?? []
      bars.push(bar)
      plannedBarsByChatJid.set(order.assignedVehicleChatJid, bars)
    }

    const chatsByJid = new Map(chats.map((chat) => [chat.jid, chat]))
    const rowsDraft: PlannerRow[] = drivers.map((driver) => {
      const baseRow = driver.chatJid
        ? baseRowsByChatJid.get(driver.chatJid)
        : undefined
      const chains = baseRow?.chains ?? []
      const plannedBars = driver.chatJid
        ? (plannedBarsByChatJid.get(driver.chatJid) ?? [])
        : []
      return {
        key: driver.jid,
        chatJid: driver.chatJid,
        plates: driver.plates,
        driverName: driver.name,
        roomName: driver.chatJid
          ? chatsByJid.get(driver.chatJid)?.name
          : undefined,
        chains,
        laneCount: Math.max(baseRow?.laneCount ?? 1, 1),
        routeBar: buildRouteBar(driver.stops, timelineStart, chains),
        plannedBars,
        companies: vehicleCompanyNames(
          chains,
          driver.chatJid
            ? (plannedOrdersByChatJid.get(driver.chatJid) ?? [])
            : [],
        ),
      }
    })

    // Always alphabetical, regardless of whether provider feeds have loaded
    // yet — sorting by "soonest free" needs feed data that only arrives after
    // a slow fetch, so ordering by it would reshuffle the whole row list the
    // moment that data lands (rows visibly jump around).
    rowsDraft.sort((left, right) =>
      (left.plates || left.driverName).localeCompare(
        right.plates || right.driverName,
      ),
    )

    const offsets = [
      ...(baseData?.dateOffsets ?? []),
      ...rowsDraft.flatMap((row) =>
        row.plannedBars.flatMap((bar) => [
          Math.floor(bar.startOffset),
          Math.ceil(bar.endOffset),
        ]),
      ),
      // Dispatcher-parked orders render in their own lanes below the fleet
      ...plannedOrders
        .filter((order) => order.assignmentStatus === "dispatcher")
        .flatMap((order) => {
          const bar = buildPlannedBar(order, timelineStart)
          return bar
            ? [Math.floor(bar.startOffset), Math.ceil(bar.endOffset)]
            : []
        }),
      // Route bars deliberately do NOT extend the date window — long-running
      // routes would drag it into the past; they are clamped at render time.
      -1,
      0,
      1,
      2,
      3,
      4,
      5,
    ]
    const minOffset = Math.max(Math.min(...offsets), -7)
    const maxOffset = Math.min(Math.max(...offsets), 14)
    const dateOffsetsDraft = Array.from(
      { length: maxOffset - minOffset + 1 },
      (_, index) => minOffset + index,
    )

    return { rows: rowsDraft, dateOffsets: dateOffsetsDraft }
  }, [baseData, drivers, chats, plannedOrders, timelineStart])

  // Provider-feed chains count as busy time too — work synced only via
  // G-Box/Navigator/Platform Science must exclude the vehicle as well. Wspólna
  // dla obu rankingów, żeby nie budować jej dwa razy.
  const chainsByChainSource = useMemo(
    () =>
      new Map(
        (baseData?.rows ?? [])
          .filter((row) => row.chatJid)
          .map((row) => [row.chatJid as string, row.chains ?? []]),
      ),
    [baseData],
  )

  // Kandydaci rankingu punktowego (a dalej dojazdu drogowego liczonego na jego
  // szczycie). Tryb przypisywania woła buildRankingCandidates sam, bo wyklucza
  // przypisywane zlecenie — wspólny jest builder, nie ta lista.
  //
  // Bez ustawionego punktu nie ma po co przeliczać całej floty przy każdym
  // przychodzącym GPS-ie ani przy każdym pollu feedów.
  const rankingCandidates = useMemo<SuggestionCandidate[]>(() => {
    if (!planningPoint) return []
    return buildRankingCandidates(
      drivers,
      plannedOrders,
      chainsByChainSource,
      timelineStart,
    )
  }, [
    planningPoint,
    drivers,
    plannedOrders,
    chainsByChainSource,
    timelineStart,
  ])

  // Assign mode: suggestion order (free window, closest approach first)
  const assignSuggestionByChatJid = useMemo(() => {
    if (!assigningOrder) return null
    // Przypisywane zlecenie nie może być własną pozycją odniesienia ani własnym
    // czasem zajętym — stąd własny zestaw kandydatów, ale z tego samego
    // buildera: różni je wyłącznie wykluczone id.
    const candidates = buildRankingCandidates(
      drivers,
      plannedOrders,
      chainsByChainSource,
      timelineStart,
      assigningOrder.id,
    )
    const suggestions = suggestVehiclesForOrder(assigningOrder, candidates)
    return new Map(
      suggestions.map((suggestion, index) => [
        suggestion.chatJid,
        { index, suggestion },
      ]),
    )
  }, [
    assigningOrder,
    drivers,
    plannedOrders,
    chainsByChainSource,
    timelineStart,
  ])

  // Ten sam kształt co assignSuggestionByChatJid, żeby render gridu czytał
  // jedną zmienną i nie rozdwajał się na dwa tryby.
  const pointSuggestionByChatJid = useMemo(() => {
    if (!planningPoint) return null
    const ranking = rankVehiclesByProximity(
      planningPoint,
      planningDay,
      rankingCandidates,
    )
    return new Map(
      ranking.map((suggestion, index) => [
        suggestion.chatJid,
        { index, suggestion },
      ]),
    )
  }, [planningPoint, planningDay, rankingCandidates])

  // Tryb przypisywania zlecenia ma pierwszeństwo: ma bogatszy kontekst (okno
  // wolne między załadunkiem a rozładunkiem konkretnego zlecenia). Punkt
  // zostaje ustawiony i wraca po wyjściu z przypisywania.
  const activeRanking = assignSuggestionByChatJid ?? pointSuggestionByChatJid

  // Czy wolno pokazać kolor wolny/zajęty dla wybranego dnia. Dla daty poza oknem
  // osi czasu hasFreeWindow jest prawdziwe dla całej floty, bo o tym dniu nie ma
  // ŻADNYCH danych — pomalowanie wtedy wszystkiego na „wolne" byłoby
  // stwierdzeniem, którego nie mamy z czego zrobić. Data zostaje bez zmian:
  // ranking odległościowy dla dnia +60 jest sensowny, a miejscowość odniesienia
  // i tak mówi, od czego liczymy.
  const isPointDayCovered = useMemo(
    () => isDateWithinTimelineWindow(planningDay, timelineStart, dateOffsets),
    [planningDay, timelineStart, dateOffsets],
  )

  // Options for the two filter selects in the filter bar. Companies are the
  // union of both sides of the view: the orders still placeable on the right
  // (unassigned or parked on a dispatcher) and the companies the vehicle rows
  // on the left already work for. Taking only the first set is what used to
  // make the filter unusable — it offered companies that, by construction,
  // no vehicle row could ever match.
  const companyOptions = useMemo(() => {
    const names = new Set<string>()
    for (const order of plannedOrders) {
      if (
        order.assignmentStatus !== "vehicle" &&
        order.metadata?.customer_name
      ) {
        names.add(order.metadata.customer_name)
      }
    }
    for (const row of rows) {
      for (const name of row.companies) names.add(name)
    }
    return Array.from(names)
      .sort()
      .map((name) => ({ label: name, value: name }))
  }, [plannedOrders, rows])

  const dispatcherOptions = useMemo(
    () =>
      dispatchers.map((profile) => ({
        label: profile.name || profile.jid,
        value: profile.jid,
      })),
    [dispatchers],
  )

  // The company filter is bypassed entirely while in vehicle-assign mode — a
  // dispatcher must be able to assign to any vehicle in the fleet regardless
  // of which company happens to be filtered.
  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesPlateFilter(
            row.plates || row.driverName,
            plannerQuery,
            row.roomName || row.driverName,
          ) &&
          (Boolean(assigningOrder) ||
            matchesVehicleCompanyFilter(companyFilter, row.companies)),
      ),
    [rows, plannerQuery, assigningOrder, companyFilter],
  )

  // Zbiór pojazdów, które dyspozytor NAPRAWDĘ widzi — z tego samego predykatu,
  // co render. Czoło rankingu do doprecyzowania dojazdem musi być z niego, bo
  // ranking liczy się dla całej floty: po zawężeniu do jednej firmy dziesiątka
  // liczona z całości trafiała w pojazdy spoza ekranu.
  const visibleRowChatJids = useMemo(
    () =>
      new Set(
        filteredRows.flatMap((row) => (row.chatJid ? [row.chatJid] : [])),
      ),
    [filteredRows],
  )

  // Tryb przypisywania zlecenia nie korzysta z dojazdu drogowego: ma własny,
  // bogatszy ranking.
  const approachOrigins = useMemo(
    () =>
      assigningOrder
        ? []
        : selectApproachOrigins(
            pointSuggestionByChatJid,
            visibleRowChatJids,
            APPROACH_TOP_N,
          ),
    [pointSuggestionByChatJid, visibleRowChatJids, assigningOrder],
  )

  const roadDistances = useApproachDistances(
    assigningOrder ? null : planningPoint,
    planningDay,
    approachOrigins,
  )

  // Assign mode shows every vehicle (not just fitting ones) so a dispatcher
  // can see the conflict, not just that a vehicle is missing — fitting
  // vehicles sort first (suggestVehiclesForOrder already orders that way);
  // rows with no suggestion data (no chatJid) sort last. Tryb punktu używa tej
  // samej ścieżki, tylko z kolejnością czysto odległościową.
  const suggestionIndexFor = (row: PlannerRow) =>
    (row.chatJid ? activeRanking?.get(row.chatJid)?.index : undefined) ??
    Number.MAX_SAFE_INTEGER
  const visibleRows = activeRanking
    ? [...filteredRows].sort(
        (left, right) => suggestionIndexFor(left) - suggestionIndexFor(right),
      )
    : filteredRows
  // Bandy „Sugerowane przez Slick AI" / „Pozostałe dostępne pojazdy" należą do
  // trybu przypisywania — w trybie punktu sortujemy czystymi kilometrami, więc
  // nie ma granicy do narysowania.
  const suggestedRowCount = assignSuggestionByChatJid
    ? visibleRows.filter(
        (row) =>
          row.chatJid &&
          assignSuggestionByChatJid.get(row.chatJid)?.suggestion.hasFreeWindow,
      ).length
    : 0

  // Dispatcher lanes: orders parked with each dispatcher, on the same axis
  const dispatcherLanes = useMemo(() => {
    const scopedOrders = scopeOrdersToDispatcher(
      plannedOrders,
      isPlannerRoleEnabled,
      currentUserJid,
    )
    const barsByJid = new Map<string, PlannerBar[]>()
    for (const order of scopedOrders) {
      if (
        order.assignmentStatus !== "dispatcher" ||
        !order.assignedDispatcherJid
      ) {
        continue
      }
      const bar = buildPlannedBar(order, timelineStart)
      if (!bar) continue
      const bars = barsByJid.get(order.assignedDispatcherJid) ?? []
      bars.push(bar)
      barsByJid.set(order.assignedDispatcherJid, bars)
    }
    return Array.from(barsByJid.entries())
      .map(([jid, bars]) => ({
        jid,
        name:
          dispatchers.find((dispatcher) => dispatcher.jid === jid)?.name || jid,
        bars: bars.sort((left, right) => left.startOffset - right.startOffset),
      }))
      .sort((left, right) => left.name.localeCompare(right.name))
  }, [
    plannedOrders,
    dispatchers,
    timelineStart,
    isPlannerRoleEnabled,
    currentUserJid,
  ])
  const dispatcherOrdersCount = dispatcherLanes.reduce(
    (sum, lane) => sum + lane.bars.length,
    0,
  )
  // The dispatcher block leads the timeline, so the vehicle rows below it need
  // a band of their own to separate the two. Without dispatchers there is
  // nothing to separate from, and in assign mode the Slick AI bands take over.
  const isDispatcherSectionVisible =
    isPlannerRoleEnabled && !assigningOrder && dispatcherLanes.length > 0

  const ghostBar = assigningOrder
    ? buildPlannedBar(assigningOrder, timelineStart)
    : null
  // Make sure the assigned order's window is inside the visible date range
  const effectiveDateOffsets = useMemo(() => {
    if (!ghostBar) return dateOffsets
    const min = Math.min(dateOffsets[0] ?? 0, Math.floor(ghostBar.startOffset))
    const max = Math.max(
      dateOffsets[dateOffsets.length - 1] ?? 0,
      Math.ceil(ghostBar.endOffset),
    )
    return Array.from({ length: max - min + 1 }, (_, index) => min + index)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOffsets, ghostBar?.startOffset, ghostBar?.endOffset])

  const startOffset = effectiveDateOffsets[0] ?? 0
  const timelineWidth = effectiveDateOffsets.length * DAY_WIDTH
  const offsetToPx = (offset: number) => (offset - startOffset) * DAY_WIDTH
  const nowOffsetPx = offsetToPx(toOffset(now, timelineStart))

  const assigningFlags = assigningOrder
    ? plannedOrderFlags(assigningOrder)
    : null

  const handleAssignToRow = (row: PlannerRow) => {
    if (!assigningOrder || !row.chatJid) return
    dispatch(assignPlannedOrderToVehicleAndSync(assigningOrder.id, row.chatJid))
    setAssigningOrderId(null)
  }

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Bump to ask for a re-center ("Pokaż dziś"). Trzymamy to w stanie, a nie w
  // wywołaniu wprost, bo scrollowanie musi się stać PO commicie — inaczej
  // liczyłoby pozycję ze starego viewDate.
  const [centerRequest, setCenterRequest] = useState(0)

  const centerOnNow = () => {
    const container = scrollContainerRef.current
    if (!container) return
    // Kolumna kierowcy jest sticky, więc realnie widoczna szerokość osi to
    // clientWidth minus ta kolumna; środek liczymy w tej szerokości.
    const visibleTimelineWidth = container.clientWidth - DRIVER_COLUMN_WIDTH
    // Przy pierwszym commicie panel bywa jeszcze nierozmierzony (clientWidth 0).
    // Liczenie środka z zera przewinęłoby oś ZA "teraz", więc wtedy odpuszczamy
    // — rAF poniżej próbuje ponownie, gdy layout już stoi.
    if (visibleTimelineWidth <= 0) return
    container.scrollLeft = Math.max(0, nowOffsetPx - visibleTimelineWidth / 2)
  }

  // Otwarcie widoku ma zawsze pokazywać "teraz" na środku osi — domyślny zakres
  // startuje dzień wstecz, więc bez tego dyspozytor ląduje na krawędzi i musi
  // scrollować. Powtarzamy przy zmianie lewej krawędzi zakresu, bo feedy
  // providerów dociągają się po zamontowaniu i mogą rozszerzyć oś w lewo, co
  // unieważnia wcześniejsze wyśrodkowanie. Strzałki dnia NIE zmieniają
  // startOffset (offsety są względne), więc nawigacja nie porywa widoku z
  // powrotem na dziś.
  useEffect(() => {
    // Dwa podejścia: teraz (zwykle wystarcza) i po najbliższej klatce, na wypadek
    // gdy panel jeszcze się mierzył. rAF anulujemy przy odmontowaniu.
    centerOnNow()
    const frame = requestAnimationFrame(centerOnNow)
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOffset, centerRequest])

  const handleUpsertOrder = (order: PlannedOrder) => {
    dispatch(savePlannedOrder(order))
  }

  const vehiclesWithPlans = rows.filter(
    (row) => row.plannedBars.length > 0,
  ).length
  // Headline count for the grid card: planned orders that already sit on a
  // vehicle, which is what the rows below it actually show.
  const assignedBarCount = rows.reduce(
    (sum, row) => sum + row.plannedBars.length,
    0,
  )

  return (
    <ResizablePanelGroup
      autoSaveId="slickshift-planner-timeline-layout"
      direction="horizontal"
      className="h-full"
    >
      <ResizablePanel defaultSize={74} minSize={50}>
        <div className="flex h-full min-h-0 flex-col bg-[var(--background-primary)]">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-[var(--panel-color)] px-4 py-2">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              <h1 className="text-base font-semibold text-foreground">
                {t("planner.timeline.title")}
              </h1>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  aria-label={t("planner.timeline.previousDay")}
                  onClick={() => setViewDate((date) => addDays(date, -1))}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="min-w-[92px] text-center text-sm font-medium text-foreground">
                  {format(viewDate, "dd.MM EEE", {
                    locale: getDateFnsLocale(),
                  })}
                </span>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  aria-label={t("planner.timeline.nextDay")}
                  onClick={() => setViewDate((date) => addDays(date, 1))}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Badge variant="secondary">
                {t("planner.timeline.vehicleCount", {
                  count: visibleRows.length,
                })}
              </Badge>
              {isPlannerRoleEnabled && (
                <Badge variant="outline">
                  {t("planner.timeline.unassignedCount", {
                    count: unassignedOrders.length,
                  })}
                </Badge>
              )}
              <Badge variant="outline">
                {t("planner.timeline.vehiclesWithPlanCount", {
                  count: vehiclesWithPlans,
                })}
              </Badge>
              {isAnyProviderEnabled && loading && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-500" />
              )}
              {isAnyProviderEnabled && !loading && timelineFeedsError && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950"
                  title={timelineFeedsError}
                  onClick={retryTimelineFeeds}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t("planner.timeline.feedsErrorRetry", {
                    error: timelineFeedsError,
                  })}
                </Button>
              )}
            </div>
            {/* Dwa rzędy: filtry i akcje u góry, "Punkt planowania" pod nimi,
                wyrównany do lewej krawędzi pierwszego dropdowna. Wciśnięcie
                punktu w jeden rząd ściskało pole współrzędnych i wyszukiwarkę
                na węższym panelu. */}
            <div className="flex flex-col items-start gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {isPlannerRoleEnabled && (
                  <>
                    <MultiSelect
                      options={companyOptions}
                      selected={companyFilter}
                      onChange={setCompanyFilter}
                      allLabel={t("planner.timeline.allCompanies")}
                      placeholder={t("planner.timeline.allCompanies")}
                      showSelectedLabels
                      className="h-8 w-44 text-xs font-normal"
                    />
                    <MultiSelect
                      options={dispatcherOptions}
                      selected={dispatcherFilter}
                      onChange={setDispatcherFilter}
                      allLabel={t("planner.timeline.allDispatchers")}
                      placeholder={t("planner.timeline.allDispatchers")}
                      showSelectedLabels
                      className="h-8 w-48 text-xs font-normal"
                    />
                  </>
                )}
                {/* One search box for the planner: the same text narrows the
                  vehicle rows and the order lists on the right. */}
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="search"
                    placeholder={t("planner.timeline.searchPlaceholder")}
                    aria-label={t("planner.timeline.searchLabel")}
                    value={plannerQuery}
                    onChange={(event) => setPlannerQuery(event.target.value)}
                    className="h-8 rounded-md bg-white pl-8 text-sm dark:bg-input/30"
                  />
                </div>
                {isPlannerRoleEnabled && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        className="bg-blue-600 text-white shadow-sm hover:bg-blue-700"
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        {t("planner.timeline.addOrder")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-52 p-1.5">
                      <input
                        ref={addOrderFileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0]
                          event.target.value = ""
                          if (file) {
                            setCreateRequest((previous) => ({
                              mode: "file",
                              seq: (previous?.seq ?? 0) + 1,
                              file,
                            }))
                          }
                        }}
                      />
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => addOrderFileInputRef.current?.click()}
                      >
                        <FilePlus2 className="mr-2 h-4 w-4" />
                        {t("planner.timeline.fromFile")}
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() =>
                          setCreateRequest((previous) => ({
                            mode: "text",
                            seq: (previous?.seq ?? 0) + 1,
                          }))
                        }
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        {t("planner.timeline.fromText")}
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() =>
                          setCreateRequest((previous) => ({
                            mode: "manual",
                            seq: (previous?.seq ?? 0) + 1,
                          }))
                        }
                      >
                        <PencilLine className="mr-2 h-4 w-4" />
                        {t("planner.timeline.manual")}
                      </Button>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              {/* "Punkt planowania": współrzędne + dzień, we własnym rzędzie pod
                  filtrami. Ustawiony punkt przesortowuje wiersze pojazdów po
                  dojeździe. Widoczny dla każdego dyspozytora — szukanie
                  najbliższego auta to nie uprawnienie, a isPlannerRoleEnabled
                  gateuje filtry i dodawanie zleceń. */}
              <div className="flex flex-wrap items-center gap-1.5">
                <div className="w-52">
                  <AutocompleteGooglePlaces
                    key={pointInputSeq}
                    placeholder={t("planner.timeline.planningPointPlaceholder")}
                    initialValue=""
                    onLocationSelect={(location) => {
                      const parsed = parsePlanningPoint(location)
                      setPlanningPoint(parsed)
                      if (parsed) {
                        setPlanningDate((current) => current ?? viewDate)
                      } else {
                        // Pole przyjmuje dowolne dwie liczby ("999, 999"), więc
                        // odrzucone współrzędne muszą zniknąć razem z badge'em
                        // — inaczej zostaje widoczny zły tekst. Datę zostawiamy,
                        // bo dyspozytor mógł ją wybrać wcześniej.
                        clearPointInput()
                      }
                    }}
                  />
                </div>
                {/* Data jest wybieralna ZAWSZE, także przed wpisaniem punktu:
                    dyspozytor zwykle wie najpierw, na kiedy szuka auta, a
                    dopiero potem wkleja współrzędne. Wybrana data przeżywa
                    ustawienie punktu (`current ?? viewDate` wyżej). */}
                <Input
                  type="date"
                  aria-label={t("planner.timeline.planningPointDate")}
                  className="h-8 w-36 text-xs"
                  value={format(planningDay, "yyyy-MM-dd")}
                  onChange={(event) => {
                    const parsed = parseISO(event.target.value)
                    if (!Number.isNaN(parsed.getTime())) {
                      setPlanningDate(startOfDay(parsed))
                    }
                  }}
                />
                {(planningPoint || planningDate) && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label={t("planner.timeline.clearPlanningPoint")}
                    onClick={() => {
                      setPlanningPoint(null)
                      setPlanningDate(null)
                      clearPointInput()
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
                {planningPoint && !assigningOrder && (
                  <Badge
                    variant="outline"
                    className="border-violet-300 text-violet-700 dark:border-violet-800 dark:text-violet-300"
                  >
                    {/* Rozpoznana miejscowość, gdy pole ją zna — dyspozytor ma
                        widzieć, WZGLĘDEM CZEGO grid jest posortowany, nie tylko
                        że jakiś punkt jest ustawiony. */}
                    {planningPoint.label
                      ? t("planner.timeline.nearestToPointWithPlace", {
                          place: planningPoint.label,
                          date: format(planningDay, "dd-MM-yyyy"),
                        })
                      : t("planner.timeline.nearestToPoint", {
                          date: format(planningDay, "dd-MM-yyyy"),
                        })}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {assigningOrder && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-violet-300 bg-violet-50 px-4 py-1.5 dark:border-violet-800 dark:bg-violet-950/60">
              <p className="min-w-0 truncate text-sm text-violet-900 dark:text-violet-100">
                <span className="font-semibold">
                  {t("planner.timeline.assigning")}
                </span>{" "}
                {assigningFlags?.from && (
                  <span aria-hidden>{assigningFlags.from} </span>
                )}
                {assigningOrder.metadata?.ref_number ||
                  t("planner.common.order")}{" "}
                • {plannedOrderRouteLabel(assigningOrder)}
                {assigningFlags?.to && (
                  <span aria-hidden> {assigningFlags.to}</span>
                )}
                <span className="ml-2 text-violet-700/80 dark:text-violet-300/80">
                  {t("planner.timeline.clickRowToAssign")}
                </span>
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAssigningOrderId(null)}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                {t("planner.common.cancel")}
              </Button>
            </div>
          )}

          {/* The grid lives in a card, the way the mockup frames it. Both the
              card and its content have to stay min-h-0 inside the resizable
              panel's flex column, or the inner scroll container grows to fit
              136 rows instead of scrolling. */}
          <Card className="m-3 flex min-h-0 flex-1 flex-col overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 border-b px-4 py-2.5">
              <CardTitle className="flex items-center gap-2 text-sm">
                {t("planner.timeline.assignedOrders")}
                <Badge variant="secondary">{assignedBarCount}</Badge>
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setViewDate(startOfDay(new Date()))
                  setCenterRequest((request) => request + 1)
                }}
              >
                {t("planner.timeline.showToday")}
              </Button>
            </CardHeader>
            <CardContent className="min-h-0 flex-1 p-0">
              <div
                ref={scrollContainerRef}
                className="relative h-full overflow-auto"
              >
                <div
                  className="relative"
                  style={{ width: DRIVER_COLUMN_WIDTH + timelineWidth }}
                >
                  {/* Header */}
                  <div className="sticky top-0 z-30 flex border-b bg-[var(--panel-color)]">
                    <div
                      className="sticky left-0 z-40 shrink-0 border-r bg-[var(--panel-color)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                      style={{ width: DRIVER_COLUMN_WIDTH }}
                    >
                      {t("planner.timeline.vehicleColumn")}
                    </div>
                    {effectiveDateOffsets.map((offset) => (
                      <div
                        key={offset}
                        className={cn(
                          "shrink-0 border-l px-2 py-1.5 text-[11px] font-semibold",
                          offset === 0
                            ? cn("text-sky-600", TODAY_COLUMN_TINT)
                            : "text-muted-foreground",
                        )}
                        style={{ width: DAY_WIDTH }}
                      >
                        {format(addDays(timelineStart, offset), "dd.MM EEE", {
                          locale: getDateFnsLocale(),
                        })}
                      </div>
                    ))}
                  </div>

                  {/* Hour ticks */}
                  <div className="sticky top-[29px] z-20 flex border-b bg-[var(--panel-color)]">
                    <div
                      className="sticky left-0 z-30 shrink-0 border-r bg-[var(--panel-color)]"
                      style={{ width: DRIVER_COLUMN_WIDTH }}
                    />
                    {effectiveDateOffsets.map((offset) => (
                      <div
                        key={offset}
                        className={cn(
                          "relative shrink-0 border-l",
                          offset === 0 && TODAY_COLUMN_TINT,
                        )}
                        style={{ width: DAY_WIDTH, height: 18 }}
                      >
                        {HOUR_TICKS.map((hour) => (
                          <span
                            key={hour}
                            className="absolute top-0 text-[9px] leading-[18px] text-muted-foreground/70"
                            style={{ left: (hour / 24) * DAY_WIDTH + 2 }}
                          >
                            {hour.toString().padStart(2, "0")}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* Dispatcher lanes: parked orders per dispatcher, same axis.
                  Planner-only — a regular dispatcher's own parked orders are
                  already in "Moje do przypisania" on the right; a second,
                  single-entry lane here just for themselves is redundant. */}
                  {isDispatcherSectionVisible && (
                    <>
                      <div
                        className={cn(
                          SECTION_BAND,
                          "bg-amber-50/70 dark:bg-amber-950/20",
                        )}
                      >
                        <button
                          type="button"
                          className={cn(
                            SECTION_BAND_LABEL,
                            "flex shrink-0 items-center gap-1 text-left text-amber-700 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200",
                          )}
                          style={{ width: DRIVER_COLUMN_WIDTH }}
                          onClick={() =>
                            setDispatcherSectionOpen((previous) => !previous)
                          }
                        >
                          {isDispatcherSectionOpen ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                          {t("planner.timeline.dispatchersSection", {
                            value: dispatcherOrdersCount.toString(),
                          })}
                        </button>
                        <div
                          className="shrink-0"
                          style={{ width: timelineWidth }}
                        />
                      </div>

                      {isDispatcherSectionOpen &&
                        dispatcherLanes.map((lane) => (
                          <div key={lane.jid} className="flex border-b">
                            <div
                              className="sticky left-0 z-20 flex shrink-0 items-center gap-2 border-r bg-[var(--panel-color)] px-3"
                              style={{
                                width: DRIVER_COLUMN_WIDTH,
                                height: ROW_MIN_HEIGHT,
                              }}
                            >
                              <UserRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <p className="min-w-0 truncate text-xs font-semibold leading-tight text-foreground">
                                {lane.name}
                              </p>
                              <Badge variant="secondary" className="ml-auto">
                                {lane.bars.length}
                              </Badge>
                            </div>

                            <div
                              className="relative shrink-0"
                              style={{
                                width: timelineWidth,
                                height: ROW_MIN_HEIGHT,
                              }}
                            >
                              {effectiveDateOffsets.map((offset) => (
                                <div
                                  key={offset}
                                  className={cn(
                                    "absolute bottom-0 top-0 border-l border-border/60",
                                    offset === 0 && TODAY_COLUMN_TINT,
                                  )}
                                  style={{
                                    left: offsetToPx(offset),
                                    width: offset === 0 ? DAY_WIDTH : undefined,
                                  }}
                                />
                              ))}
                              {nowOffsetPx >= 0 &&
                                nowOffsetPx <= timelineWidth && (
                                  <div
                                    className="pointer-events-none absolute bottom-0 top-0 z-10 w-px bg-sky-400/90"
                                    style={{ left: nowOffsetPx }}
                                  />
                                )}
                              {lane.bars.map((bar) => {
                                const left = offsetToPx(bar.startOffset)
                                const width = Math.max(
                                  offsetToPx(bar.endOffset) - left,
                                  56,
                                )
                                const barFlags = plannedOrderFlags(bar.order)
                                return (
                                  <TimelineBar
                                    key={bar.order.id}
                                    left={left}
                                    top={(ROW_MIN_HEIGHT - BAR_HEIGHT) / 2}
                                    width={width}
                                    label={
                                      barLabel(
                                        orderReferenceNumber(bar.order),
                                        plannedOrderRouteLabel(bar.order),
                                      ) || t("planner.timeline.planFallback")
                                    }
                                    title={plannedOrderRouteLabel(bar.order)}
                                    tone="border-dashed border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
                                    startFlag={barFlags.from}
                                    endFlag={barFlags.to}
                                    isSelected={
                                      bar.order.id === selectedOrderId
                                    }
                                    onClick={() =>
                                      setSelectedOrderId(bar.order.id)
                                    }
                                  />
                                )
                              })}
                            </div>
                          </div>
                        ))}
                    </>
                  )}

                  {isDispatcherSectionVisible && visibleRows.length > 0 && (
                    <div className={cn(SECTION_BAND, "bg-muted/60")}>
                      <div
                        className={cn(
                          SECTION_BAND_LABEL,
                          "text-muted-foreground",
                        )}
                      >
                        {t("planner.timeline.vehiclesSection", {
                          value: visibleRows.length.toString(),
                        })}
                      </div>
                    </div>
                  )}

                  {/* Rows */}
                  {visibleRows.map((row, rowIndex) => {
                    // Lanes stack: provider chains, then the Slick route bar,
                    // then planned orders — each only takes a lane when present.
                    const chainLanes = row.chains.length > 0 ? row.laneCount : 0
                    const routeLane = row.routeBar ? chainLanes : -1
                    const plannedLane =
                      row.plannedBars.length > 0
                        ? chainLanes + (row.routeBar ? 1 : 0)
                        : -1
                    const totalLanes = Math.max(
                      chainLanes +
                        (row.routeBar ? 1 : 0) +
                        (row.plannedBars.length > 0 ? 1 : 0),
                      1,
                    )
                    const rowHeight = Math.max(
                      ROW_MIN_HEIGHT,
                      ROW_PADDING_Y * 2 + totalLanes * LANE_SPACING,
                    )

                    const rankingEntry =
                      activeRanking && row.chatJid
                        ? activeRanking.get(row.chatJid)
                        : undefined
                    // Tryb przypisywania nigdy nie pokazywał podpisu dla pojazdu
                    // bez policzonej odległości i nie ma zacząć: "0 km od
                    // załadunku" byłoby zmyśleniem. Tryb punktu ma na to własną
                    // odpowiedź ("brak pozycji"), więc tam podpis zostaje.
                    const showRankingLabel = Boolean(
                      rankingEntry &&
                        (!assigningOrder ||
                          rankingEntry.suggestion.distanceKm !== undefined),
                    )
                    // „Wolny" wolno twierdzić tylko o dniu, o którym mamy dane:
                    // tryb przypisywania zna okno zlecenia, tryb punktu — dzień
                    // w oknie osi czasu. Poza nim zostaje kolor neutralny.
                    // („Zajęty" zostaje kolorem zawsze: bierze się z konkretnego
                    // planowanego zlecenia albo trasy, więc jest wiedzą, a nie
                    // brakiem danych — i te nie są przycięte do okna.)
                    const canClaimFree =
                      Boolean(assigningOrder) || isPointDayCovered
                    // A row is a click target only while assigning, only for a
                    // vehicle with a free window — a conflicting vehicle shows
                    // the same slot in red, but it isn't clickable. Czytane z
                    // rankingu przypisywania, nie z activeRanking: punkt
                    // planowania nie robi z wierszy celów kliknięcia.
                    const isAssignTarget =
                      Boolean(assigningOrder) &&
                      Boolean(
                        row.chatJid &&
                          assignSuggestionByChatJid?.get(row.chatJid)
                            ?.suggestion.hasFreeWindow,
                      )
                    return (
                      <Fragment key={row.key}>
                        {assigningOrder &&
                          suggestedRowCount > 0 &&
                          rowIndex === 0 && (
                            <div
                              className={cn(
                                SECTION_BAND,
                                "bg-violet-50/70 dark:bg-violet-950/20",
                              )}
                            >
                              <div
                                className={cn(
                                  SECTION_BAND_LABEL,
                                  "flex items-center gap-1.5 text-violet-700 dark:text-violet-400",
                                )}
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                {t("planner.timeline.suggestedBySlickAi")}
                              </div>
                            </div>
                          )}
                        {assigningOrder &&
                          suggestedRowCount > 0 &&
                          suggestedRowCount < visibleRows.length &&
                          rowIndex === suggestedRowCount && (
                            <div className={cn(SECTION_BAND, "bg-muted/60")}>
                              <div
                                className={cn(
                                  SECTION_BAND_LABEL,
                                  "text-muted-foreground",
                                )}
                              >
                                {t("planner.timeline.otherAvailableVehicles")}
                              </div>
                            </div>
                          )}
                        <div className="flex border-b">
                          <div
                            className="sticky left-0 z-20 flex shrink-0 items-center gap-2 border-r bg-[var(--panel-color)] px-3"
                            style={{
                              width: DRIVER_COLUMN_WIDTH,
                              height: rowHeight,
                            }}
                          >
                            <Truck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold leading-tight text-foreground">
                                {row.plates || row.roomName || row.driverName}
                              </p>
                              <div className="flex min-w-0 items-center gap-1">
                                {showRankingLabel && rankingEntry ? (
                                  <p
                                    className={`truncate text-[11px] leading-tight ${
                                      !rankingEntry.suggestion.hasFreeWindow
                                        ? "text-rose-600 dark:text-rose-400"
                                        : canClaimFree
                                          ? "text-violet-700 dark:text-violet-300"
                                          : "text-muted-foreground"
                                    }`}
                                    title={
                                      !rankingEntry.suggestion.hasFreeWindow
                                        ? assigningOrder
                                          ? t(
                                              "planner.timeline.busyInOrderWindow",
                                            )
                                          : t(
                                              "planner.timeline.busyOnSelectedDay",
                                            )
                                        : canClaimFree
                                          ? undefined
                                          : t(
                                              "planner.timeline.dayOutsideWindow",
                                            )
                                    }
                                  >
                                    {!assigningOrder
                                      ? approachLabel(
                                          rankingEntry.suggestion,
                                          roadDistances.get(
                                            rankingEntry.suggestion.chatJid,
                                          ),
                                        )
                                      : // Nigdy nie wolno wypisać "0 km od
                                        // załadunku" dla pojazdu, o którym nic
                                        // nie wiemy — brak odległości nie ma
                                        // podstawionego zera.
                                        rankingEntry.suggestion.distanceKm !==
                                          undefined
                                        ? rankingEntry.suggestion.referenceLabel
                                          ? t(
                                              "planner.timeline.kmFromPickupWithPlace",
                                              {
                                                km: Math.round(
                                                  rankingEntry.suggestion
                                                    .distanceKm,
                                                ),
                                                place:
                                                  rankingEntry.suggestion
                                                    .referenceLabel,
                                              },
                                            )
                                          : t("planner.timeline.kmFromPickup", {
                                              km: Math.round(
                                                rankingEntry.suggestion
                                                  .distanceKm,
                                              ),
                                            })
                                        : null}
                                  </p>
                                ) : (
                                  <p className="truncate text-[11px] leading-tight text-muted-foreground">
                                    {row.driverName}
                                    {" · "}
                                    {orderCountLabel(
                                      row.chains.length +
                                        row.plannedBars.length,
                                    )}
                                  </p>
                                )}
                                {rankingEntry?.suggestion.hasLateStop && (
                                  <span
                                    className="shrink-0"
                                    title={t(
                                      "planner.timeline.driverMayBeLate",
                                    )}
                                  >
                                    <AlertTriangle
                                      className="h-3 w-3 text-amber-500"
                                      aria-label={t(
                                        "planner.timeline.driverMayBeLate",
                                      )}
                                    />
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div
                            className="relative shrink-0"
                            style={{ width: timelineWidth, height: rowHeight }}
                          >
                            {effectiveDateOffsets.map((offset) => (
                              <div
                                key={offset}
                                className={cn(
                                  "absolute bottom-0 top-0 border-l border-border/60",
                                  offset === 0 && TODAY_COLUMN_TINT,
                                )}
                                style={{
                                  left: offsetToPx(offset),
                                  width: offset === 0 ? DAY_WIDTH : undefined,
                                }}
                              />
                            ))}

                            {nowOffsetPx >= 0 &&
                              nowOffsetPx <= timelineWidth && (
                                <div
                                  className="pointer-events-none absolute bottom-0 top-0 z-10 w-px bg-sky-400/90"
                                  style={{ left: nowOffsetPx }}
                                />
                              )}

                            {/* Assign-mode slot: green + clickable when this
                            vehicle is free for the order's window, red and
                            inert when it would conflict. */}
                            {assigningOrder && ghostBar && (
                              <button
                                type="button"
                                disabled={!isAssignTarget}
                                title={
                                  isAssignTarget
                                    ? t("planner.timeline.assignVehicle")
                                    : t("planner.timeline.routeConflict")
                                }
                                onClick={() =>
                                  isAssignTarget && handleAssignToRow(row)
                                }
                                className={`absolute z-[6] flex items-center justify-center overflow-hidden rounded border-2 border-dashed text-[11px] font-semibold transition-colors ${
                                  isAssignTarget
                                    ? "cursor-pointer border-green-400 bg-green-200/50 hover:border-green-500 hover:bg-green-300/70 dark:border-green-500 dark:bg-green-900/40 dark:hover:bg-green-800/60"
                                    : "cursor-default border-rose-400 bg-rose-200/50 dark:border-rose-500 dark:bg-rose-900/40"
                                }`}
                                style={{
                                  left: offsetToPx(ghostBar.startOffset),
                                  top: (rowHeight - BAR_HEIGHT) / 2,
                                  width: Math.max(
                                    offsetToPx(ghostBar.endOffset) -
                                      offsetToPx(ghostBar.startOffset),
                                    56,
                                  ),
                                  height: BAR_HEIGHT,
                                }}
                              >
                                <span
                                  className={`pointer-events-none truncate px-2 ${
                                    isAssignTarget
                                      ? "text-green-800 dark:text-green-300"
                                      : "text-rose-800 dark:text-rose-300"
                                  }`}
                                >
                                  {isAssignTarget
                                    ? t("planner.timeline.assignVehicle")
                                    : t("planner.timeline.conflict")}
                                </span>
                              </button>
                            )}

                            {row.chains.map((chain) => {
                              const left = offsetToPx(chain.startOffset)
                              const width = Math.max(
                                offsetToPx(chain.endOffset) - left,
                                56,
                              )
                              return (
                                <TimelineBar
                                  key={chain.key}
                                  left={left}
                                  top={
                                    ROW_PADDING_Y + chain.lane * LANE_SPACING
                                  }
                                  width={width}
                                  // A chain's subtitle is where buildChainSubtitle
                                  // puts the TMS reference, so it leads the label.
                                  label={barLabel(chain.subtitle, chain.label)}
                                  title={`${chain.label}${chain.subtitle ? ` • ${chain.subtitle}` : ""}`}
                                  tone={chainToneClasses[chain.tone]}
                                  startFlag={chainFlag(chain.startCode)}
                                  endFlag={chainFlag(chain.endCode)}
                                  // Same `now` the now-line is drawn from, and
                                  // measured on this bar's own axis, so the two
                                  // are comparable: a fill edge short of the
                                  // line means behind plan, past it means ahead.
                                  progress={chainProgress(chain, now)}
                                  onClick={() =>
                                    setSelectedChain({
                                      chain,
                                      rowLabel: `${row.plates || row.roomName || ""} ${row.driverName}`,
                                    })
                                  }
                                />
                              )
                            })}

                            {row.routeBar &&
                              row.routeBar.segments.map(
                                (segment, segmentIndex) => {
                                  // Clamp to the visible window instead of extending it
                                  const left = Math.max(
                                    offsetToPx(segment.startOffset),
                                    0,
                                  )
                                  const right = Math.min(
                                    offsetToPx(segment.endOffset),
                                    timelineWidth,
                                  )
                                  if (right <= left) return null
                                  // While provider feeds are still loading, this bar
                                  // may still be replaced/trimmed once chains land
                                  // — style it as provisional so it doesn't read as
                                  // finished, richer-detail-incoming data.
                                  const isProvisional =
                                    isAnyProviderEnabled && loading
                                  // Every label here is the segment's own: the stops
                                  // it spans, their count, and lateness read off
                                  // those stops — a segment that borrowed the whole
                                  // route's ends claimed work the chains draw.
                                  const stopsLabel = t(
                                    "planner.timeline.routeWithStops",
                                    {
                                      stops: stopCountLabel(segment.stopCount),
                                      label: segment.label,
                                    },
                                  )
                                  return (
                                    <TimelineBar
                                      key={segmentIndex}
                                      left={left}
                                      top={
                                        ROW_PADDING_Y + routeLane * LANE_SPACING
                                      }
                                      width={Math.max(right - left, 24)}
                                      // The Slick route carries no reference number —
                                      // it is stops sent to the driver, not a TMS
                                      // transport — so this half stays route-only.
                                      label={segment.label}
                                      title={
                                        isProvisional
                                          ? t(
                                              "planner.timeline.loadingRouteDetails",
                                              { details: stopsLabel },
                                            )
                                          : segment.isLate
                                            ? t(
                                                "planner.timeline.driverMayBeLateWithDetails",
                                                { details: stopsLabel },
                                              )
                                            : stopsLabel
                                      }
                                      tone={
                                        isProvisional
                                          ? "animate-pulse border-dashed border-slate-300/70 bg-slate-200/40 text-slate-400 dark:border-slate-600/70 dark:bg-slate-700/30 dark:text-slate-500"
                                          : "border-slate-300 bg-slate-200/80 text-slate-700 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-200"
                                      }
                                      // A provisional bar is about to be replaced, so
                                      // it shouldn't also shout "late".
                                      isLate={!isProvisional && segment.isLate}
                                      startFlag={chainFlag(
                                        segment.startCountryCode,
                                      )}
                                      endFlag={chainFlag(
                                        segment.endCountryCode,
                                      )}
                                      trailing={
                                        isProvisional ? (
                                          <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                                        ) : undefined
                                      }
                                      // No TMS order sits behind this bar, so
                                      // there is none to open — it opens the
                                      // stops it stands for instead. A bar that
                                      // may still be re-cut once feeds land
                                      // stays inert.
                                      onClick={
                                        isProvisional
                                          ? undefined
                                          : () =>
                                              setSelectedRouteSegment({
                                                stops: segment.stops,
                                                rowLabel: `${row.plates || row.roomName || ""} ${row.driverName}`,
                                              })
                                      }
                                    />
                                  )
                                },
                              )}

                            {row.plannedBars.map((bar) => {
                              const left = offsetToPx(bar.startOffset)
                              const width = Math.max(
                                offsetToPx(bar.endOffset) - left,
                                56,
                              )
                              const barFlags = plannedOrderFlags(bar.order)
                              return (
                                <TimelineBar
                                  key={bar.order.id}
                                  left={left}
                                  top={
                                    ROW_PADDING_Y + plannedLane * LANE_SPACING
                                  }
                                  width={width}
                                  label={
                                    barLabel(
                                      orderReferenceNumber(bar.order),
                                      plannedOrderRouteLabel(bar.order),
                                    ) || t("planner.timeline.planFallback")
                                  }
                                  title={plannedOrderRouteLabel(bar.order)}
                                  tone="border-dashed border-violet-400 bg-violet-50 text-violet-900 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950 dark:text-violet-100"
                                  startFlag={barFlags.from}
                                  endFlag={barFlags.to}
                                  isSelected={bar.order.id === selectedOrderId}
                                  onClick={() =>
                                    setSelectedOrderId(bar.order.id)
                                  }
                                />
                              )
                            })}
                          </div>
                        </div>
                      </Fragment>
                    )
                  })}

                  {visibleRows.length === 0 && (
                    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                      {companyFilter.length > 0 && !assigningOrder
                        ? t("planner.timeline.noVehiclesForCompany")
                        : t("planner.timeline.noVehicles")}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      <ResizablePanel defaultSize={26} minSize={18}>
        {selectedOrder ? (
          <PlannedOrderDetailPanel
            order={selectedOrder}
            onClose={() => setSelectedOrderId(null)}
            dispatchers={dispatchers}
            assignedCountByDispatcher={assignedCountByDispatcher}
            assignedVehicleLabel={(() => {
              if (!selectedOrder.assignedVehicleChatJid) return undefined
              const vehicle = drivers.find(
                (driver) =>
                  driver.chatJid === selectedOrder.assignedVehicleChatJid,
              )
              if (!vehicle) return undefined
              return vehicle.plates
                ? `${vehicle.plates} • ${vehicle.name}`
                : vehicle.name
            })()}
            onAssignToDispatcher={(id, dispatcherJid) =>
              dispatch(assignPlannedOrderToDispatcherAndSync(id, dispatcherJid))
            }
            onPickVehicle={(id) => {
              setAssigningOrderId(id)
              // Jump the timeline to the order's pickup date so free/busy
              // vehicle windows are actually visible instead of the current
              // (often unrelated) day.
              const pickupTime = selectedOrder
                ? parseTime(
                    firstPickupStop(selectedOrder)?.scheduled_arrival_time,
                  )
                : undefined
              if (pickupTime) setViewDate(startOfDay(pickupTime))
            }}
            onUnassign={(id) => dispatch(unassignPlannedOrderAndSync(id))}
            isPlannerRoleEnabled={isPlannerRoleEnabled}
            onOrderUpdate={(updatedOrder) =>
              dispatch(
                enrichPlannedOrderStops(updatedOrder.id, updatedOrder.stops),
              )
            }
          />
        ) : (
          <PlannedOrdersPanel
            unassignedOrders={unassignedOrders}
            myOrders={myOrders}
            dispatcherAssignedOrders={dispatcherAssignedOrders}
            dispatchers={dispatchers}
            assignedCountByDispatcher={assignedCountByDispatcher}
            currentUserJid={currentUserJid}
            isPlannerRoleEnabled={isPlannerRoleEnabled}
            companyFilter={companyFilter}
            onCompanyFilterChange={setCompanyFilter}
            dispatcherFilter={dispatcherFilter}
            onDispatcherFilterChange={setDispatcherFilter}
            query={plannerQuery}
            onQueryChange={setPlannerQuery}
            onSelectOrder={(order) => setSelectedOrderId(order.id)}
          />
        )}
      </ResizablePanel>

      <PlannedOrderCreateFlow
        key={createRequest?.seq ?? 0}
        mode={createRequest?.mode ?? null}
        initialFile={createRequest?.file ?? null}
        onExit={() => setCreateRequest(null)}
        onSaved={handleUpsertOrder}
      />

      <Dialog
        open={Boolean(selectedChain)}
        onOpenChange={(open) => {
          if (!open) setSelectedChain(null)
        }}
      >
        <DialogContent className="max-h-[80vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto">
          {selectedChain && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedChain.chain.label}</DialogTitle>
                <DialogDescription>{selectedChain.rowLabel}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                {selectedChain.chain.events.map((event) => {
                  // gps's timeline builders now backfill this from a
                  // coordinate-matched route2g stop server-side when the
                  // provider that built this chain didn't sync this stop
                  // itself (e.g. a Navigator chain over a GBox-tracked
                  // transport) — no client-side fallback needed anymore.
                  const timelinessStatus = event.timelinessStatus
                  const estimatedArrivalTime =
                    event.estimatedArrivalTimeWithTacho ??
                    event.estimatedArrivalTime
                  const scheduledTime = event.scheduledTime
                  const isLate = timelinessStatus === "late"
                  const scheduledDate = scheduledTime
                    ? parseISO(scheduledTime)
                    : null
                  const dateTimeLabel =
                    scheduledDate && !Number.isNaN(scheduledDate.getTime())
                      ? format(scheduledDate, "dd.MM HH:mm")
                      : event.timeLabel
                  const estimatedArrivalDate = estimatedArrivalTime
                    ? parseISO(estimatedArrivalTime)
                    : null
                  const etaTimeLabel =
                    estimatedArrivalDate &&
                    !Number.isNaN(estimatedArrivalDate.getTime())
                      ? format(estimatedArrivalDate, "HH:mm")
                      : null
                  const delayLabel =
                    isLate && estimatedArrivalDate && scheduledTime
                      ? formatDuration(
                          Math.floor(
                            (estimatedArrivalDate.getTime() -
                              parseISO(scheduledTime).getTime()) /
                              1000,
                          ),
                        )
                      : null

                  return (
                    <div
                      key={`${event.chainId}-${event.eventId}`}
                      className="flex items-center justify-between gap-2 rounded border px-2.5 py-1.5 text-xs"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{event.title}</p>
                        <p className="truncate text-muted-foreground">
                          {event.kindLabel}
                          {event.referenceNumber &&
                            ` • ${event.referenceNumber}`}
                        </p>
                      </div>
                      <div className="shrink-0 text-right text-muted-foreground">
                        <p>{dateTimeLabel}</p>
                        {timelinessStatus && (
                          <p
                            className={
                              isLate ? "text-rose-500" : "text-emerald-600"
                            }
                          >
                            {isLate
                              ? t("planner.timeline.delayed")
                              : t("planner.timeline.onTime")}
                          </p>
                        )}
                        {isLate && etaTimeLabel && (
                          <p className="text-rose-500">
                            {t("planner.common.eta", { time: etaTimeLabel })}
                            {delayLabel && ` (${delayLabel})`}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <RouteSegmentDialog
        open={Boolean(selectedRouteSegment)}
        onOpenChange={(open) => {
          if (!open) setSelectedRouteSegment(null)
        }}
        rowLabel={selectedRouteSegment?.rowLabel ?? ""}
        stops={selectedRouteSegment?.stops ?? []}
      />
    </ResizablePanelGroup>
  )
}

export default PlannerTimelineView
